import {
  buildCanonicalQuickNoteSavePayload,
  getTimezoneMetadata,
} from './quickLogSavePayload';
import {
  buildCaptureTimelineFields,
  createCapture,
} from '../features/captures';

const createTimelineEvent = (eventName, detail, createCustomEvent) => (
  typeof createCustomEvent === 'function'
    ? createCustomEvent(eventName, detail)
    : typeof CustomEvent !== 'undefined'
      ? new CustomEvent(eventName, { detail })
      : { type: eventName, detail }
);

const dispatchTimelineEvent = ({ eventName, detail, dispatchEvent, createCustomEvent }) => {
  const event = createTimelineEvent(eventName, detail, createCustomEvent);

  if (typeof dispatchEvent === 'function') {
    dispatchEvent(event);
  } else if (typeof window !== 'undefined' && typeof window.dispatchEvent === 'function') {
    window.dispatchEvent(event);
  }
};

export const saveDirectQuickNote = async ({
  child = null,
  text = '',
  selectedDate = new Date(),
  selectedTime = '',
  resolvedCategory = 'log',
  importantMoment = false,
  user = null,
  noteData = {},
  tags = [],
  transcriptNormalization = {},
  isEditMode = false,
  entryId = null,
  initialData = null,
  photoFile = null,
  photoIntent = '',
  audioBlob = null,
  deps = {},
} = {}) => {
  const canonicalText = String(text || '').trim();
  const hasContent = Boolean(canonicalText || photoFile || audioBlob);
  if (!hasContent) {
    return null;
  }

  const {
    db,
    collection,
    addDoc,
    doc,
    serverTimestamp,
    updateDoc,
    uploadIncidentMedia,
    dispatchEvent,
    createCustomEvent,
    createCapture: createCaptureDep,
  } = deps;

  const { timeZoneName, timeZoneOffsetMinutes } = getTimezoneMetadata();
  const optimisticEntry = buildCanonicalQuickNoteSavePayload({
    child,
    text: canonicalText,
    selectedDate,
    selectedTime,
    resolvedCategory,
    importantMoment,
    user,
    noteData,
    tags,
    parserMode: 'quick_log',
    sourceMode: 'quick_log',
    inputMode: 'quick_log',
    metadata: {
      captureMode: 'simple_local',
      routingReason: 'simple_local',
      localConfidence: 'high',
      transcriptNormalization,
    },
  });

  const docPayload = {
    childId: child?.id,
    createdBy: user?.uid,
    createdAt: serverTimestamp(),
    ...optimisticEntry,
    timeZoneName,
    timeZoneOffsetMinutes,
    entryDate: optimisticEntry.localDate,
  };

  let docRef = null;
  let captureContext = null;
  let captureTimelineFields = {};

  if (isEditMode && (entryId || initialData?.id)) {
    const targetId = entryId || initialData?.id;
    docRef = { id: targetId };
    await updateDoc(doc(db, 'dailyLogs', targetId), {
      ...docPayload,
      editedByUser: true,
      updatedAt: serverTimestamp(),
    });
  } else {
    const captureCreator = createCaptureDep || createCapture;
    captureContext = await captureCreator({
      childId: child?.id || '',
      userId: user?.uid || '',
      sourceType: photoFile || audioBlob ? 'quick_log_media' : 'quick_log',
      sourceText: canonicalText,
      originalText: transcriptNormalization?.originalText || canonicalText,
      inputContext: {
        entryPoint: 'dashboard_quick_log',
        hasPhoto: Boolean(photoFile),
        hasAudio: Boolean(audioBlob),
      },
      metadata: {
        resolvedCategory,
        importantMoment,
        ...(photoFile && photoIntent ? { photoIntent } : {}),
      },
    }, deps);
    captureTimelineFields = buildCaptureTimelineFields(captureContext);
    docRef = await addDoc(collection(db, 'dailyLogs'), {
      ...docPayload,
      ...captureTimelineFields,
      ...(photoFile && photoIntent ? { photoIntent } : {}),
    });
  }

  let mediaUrls = [];

  if (photoFile || audioBlob) {
    const uploadedRichMedia = await uploadIncidentMedia(
      photoFile ? { file: photoFile, type: 'image' } : null,
      audioBlob,
      docRef.id,
      `dailyLogs/${docRef.id}`
    );
    mediaUrls = [
      ...mediaUrls,
      ...(uploadedRichMedia || []).map((item) => item.url).filter(Boolean),
    ];
  }

  if (mediaUrls.length > 0) {
    await updateDoc(docRef, { mediaUrls });
  }

  if (!isEditMode) {
    dispatchTimelineEvent({
      eventName: 'captureez:timeline-entry-created',
      detail: {
        ...docPayload,
        ...captureTimelineFields,
        id: docRef.id,
        entryId: docRef.id,
        collection: 'dailyLogs',
        ...(mediaUrls.length > 0 && { mediaUrls }),
      },
      dispatchEvent,
      createCustomEvent,
    });
  }

  const refreshDetail = {
    childId: child?.id,
    entryId: docRef.id,
    collection: 'dailyLogs',
    entryDate: optimisticEntry.localDate || null,
  };
  dispatchTimelineEvent({
    eventName: 'captureez:timeline-refresh',
    detail: refreshDetail,
    dispatchEvent,
    createCustomEvent,
  });

  const completionPayload = {
    text: canonicalText,
    tags,
    importantMoment,
    category: resolvedCategory,
    timestamp: optimisticEntry.timestamp,
    timestampUtc: optimisticEntry.timestampUtc,
    localDate: optimisticEntry.localDate,
    localTime: optimisticEntry.localTime,
    timeZoneName,
    timeZoneOffsetMinutes,
    timestampSource: 'local-input',
    entryDate: optimisticEntry.localDate,
    entryId: docRef.id,
    mode: isEditMode ? 'edit' : 'create',
    ...(captureContext?.captureId ? { captureId: captureContext.captureId } : {}),
    ...(mediaUrls.length > 0 && { mediaUrls }),
  };

  return {
    docRef,
    docPayload,
    optimisticEntry,
    mediaUrls,
    completionPayload,
  };
};
